import React, { useMemo, useState } from "react";

/**
 * CareLogix™ ORACLE — Retirement Compound Calculator (Phase 16, LOGIX-2™ Era)
 * Strictly additive. Renders inside the "retire" mode panel above the chat.
 * Hands a fully-composed prompt to the parent via `onQuery` so the existing
 * /api/oracle/chat pipeline drives the actual LOGIX-2 analysis.
 */
export default function RetirementCalculator({ onQuery, disabled = false }) {
  const [currentAge, setCurrentAge] = useState(35);
  const [retireAge, setRetireAge] = useState(65);
  const [currentSaved, setCurrentSaved] = useState(75000);
  const [monthlyContrib, setMonthlyContrib] = useState(800);
  const [employerMatch, setEmployerMatch] = useState(3);
  const [annualReturn, setAnnualReturn] = useState(8);
  const [accountType, setAccountType] = useState("401k_roth");

  const calc = useMemo(() => {
    const years = Math.max(retireAge - currentAge, 1);
    const r = annualReturn / 100 / 12;
    const n = years * 12;
    const matchContrib = monthlyContrib * (employerMatch / 100);
    const totalMonthly = monthlyContrib + matchContrib;
    const fvLump = currentSaved * Math.pow(1 + r, n);
    const fvAnnuity = r === 0 ? totalMonthly * n : totalMonthly * ((Math.pow(1 + r, n) - 1) / r);
    const total = fvLump + fvAnnuity;
    const totalContribs = currentSaved + totalMonthly * n;
    const growthGain = total - totalContribs;
    const inflAdj = total / Math.pow(1.03, years);
    const monthly4pct = (total * 0.04) / 12;
    const rule72 = annualReturn > 0 ? (72 / annualReturn).toFixed(1) : "—";

    const chartPoints = Array.from({ length: 10 }, (_, i) => {
      const yr = Math.round((years / 9) * i);
      const ny = yr * 12;
      const v =
        currentSaved * Math.pow(1 + r, ny) +
        (r === 0 ? totalMonthly * ny : totalMonthly * ((Math.pow(1 + r, ny) - 1) / r));
      return { yr, val: v };
    });
    return {
      years,
      total,
      totalContribs,
      growthGain,
      inflAdj,
      monthly4pct,
      rule72,
      matchContribTotal: matchContrib * n,
      chartPoints,
      chartMax: chartPoints[chartPoints.length - 1].val || 1,
    };
  }, [currentAge, retireAge, currentSaved, monthlyContrib, employerMatch, annualReturn]);

  const fmt = (v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${Math.round(v).toLocaleString()}`);

  const verdict = calc.total >= 1_000_000 ? "ON TRACK" : calc.total >= 500_000 ? "NEEDS ATTENTION" : "CRITICAL SHORTFALL";
  const verdictTone = calc.total >= 1_000_000 ? "ok" : calc.total >= 500_000 ? "warn" : "bad";

  const runAnalysis = () => {
    const prompt =
      `I am ${currentAge} years old, targeting retirement at ${retireAge}. ` +
      `I have $${currentSaved.toLocaleString()} saved in a ${accountType.toUpperCase().replace("_", " ")} account, ` +
      `contributing $${monthlyContrib}/month with ${employerMatch}% employer match, ` +
      `expecting ${annualReturn}% annual return. ` +
      `My projected nest egg is ${fmt(calc.total)}. ` +
      `Run a full LOGIX-2 Retirement Intelligence Report — include optimization levers, tax strategy, risk scenarios, and confidence score.`;
    onQuery?.(prompt);
  };

  return (
    <div className="retire-calc" data-testid="retire-calculator">
      <div className="retire-calc__hd">
        <span className="retire-calc__hd-icon" aria-hidden="true">🏦</span>
        <div className="retire-calc__hd-titles">
          <div className="retire-calc__hd-title">Retirement Compound Calculator</div>
          <div className="retire-calc__hd-sub">CareLogix™ ORACLE · LOGIX-2™ · Nest-Egg Projector</div>
        </div>
        <span className={`retire-calc__verdict retire-calc__verdict--${verdictTone}`} data-testid="retire-verdict">
          {verdict}
        </span>
      </div>

      <div className="retire-calc__grid">
        <label className="retire-field">
          <span>Current Age</span>
          <input
            type="number"
            min={18}
            max={75}
            value={currentAge}
            onChange={(e) => setCurrentAge(+e.target.value || 0)}
            data-testid="retire-current-age"
          />
        </label>
        <label className="retire-field">
          <span>Retirement Age</span>
          <input
            type="number"
            min={45}
            max={85}
            value={retireAge}
            onChange={(e) => setRetireAge(+e.target.value || 0)}
            data-testid="retire-retire-age"
          />
        </label>
        <label className="retire-field">
          <span>Account Type</span>
          <select
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            data-testid="retire-account-type"
          >
            <option value="401k_roth">Roth 401(k)</option>
            <option value="401k_trad">Traditional 401(k)</option>
            <option value="ira_roth">Roth IRA</option>
            <option value="ira_trad">Traditional IRA</option>
            <option value="taxable">Taxable Brokerage</option>
            <option value="sep_ira">SEP-IRA</option>
            <option value="hsa">HSA</option>
          </select>
        </label>
        <label className="retire-field">
          <span>Current Savings ($)</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={currentSaved}
            onChange={(e) => setCurrentSaved(+e.target.value || 0)}
            data-testid="retire-current-saved"
          />
        </label>
        <label className="retire-field">
          <span>Monthly Contribution ($)</span>
          <input
            type="number"
            min={0}
            step={50}
            value={monthlyContrib}
            onChange={(e) => setMonthlyContrib(+e.target.value || 0)}
            data-testid="retire-monthly-contrib"
          />
        </label>
        <label className="retire-field">
          <span>Employer Match (%)</span>
          <input
            type="number"
            min={0}
            max={15}
            step={0.5}
            value={employerMatch}
            onChange={(e) => setEmployerMatch(+e.target.value || 0)}
            data-testid="retire-employer-match"
          />
        </label>
      </div>

      <div className="retire-slider">
        <div className="retire-slider__row">
          <span>Expected Annual Return</span>
          <strong data-testid="retire-return-value">{annualReturn}%</strong>
        </div>
        <input
          type="range"
          min={2}
          max={14}
          step={0.5}
          value={annualReturn}
          onChange={(e) => setAnnualReturn(+e.target.value)}
          data-testid="retire-return-slider"
        />
        <div className="retire-slider__chips">
          {[
            ["Conservative", 4],
            ["Balanced", 7],
            ["Aggressive", 10],
            ["S&P long-run", 12],
          ].map(([lbl, v]) => (
            <button
              key={lbl}
              type="button"
              className="retire-chip"
              onClick={() => setAnnualReturn(v)}
              data-testid={`retire-chip-${v}`}
            >
              {lbl} {v}%
            </button>
          ))}
        </div>
      </div>

      <div className="retire-result">
        <div className="retire-result__hero" data-testid="retire-nest-egg">
          <span className="retire-result__label">Projected Nest Egg at Age {retireAge}</span>
          <span className="retire-result__value">{fmt(calc.total)}</span>
          <span className="retire-result__sub">
            Inflation-adjusted: <b>{fmt(calc.inflAdj)}</b> · Monthly income (4% rule): <b>{fmt(calc.monthly4pct)}/mo</b>
          </span>
        </div>
        <div className="retire-result__grid">
          <div><span>Total Contributions</span><b>{fmt(calc.totalContribs)}</b></div>
          <div><span>Compound Growth</span><b className="retire-pos">{fmt(calc.growthGain)}</b></div>
          <div><span>Employer Match Total</span><b>{fmt(calc.matchContribTotal)}</b></div>
          <div><span>Rule of 72 — Doubles every</span><b>{calc.rule72} yrs</b></div>
        </div>
      </div>

      <div className="retire-chart" aria-label="Growth milestones">
        {calc.chartPoints.map((pt, i) => {
          const h = calc.chartMax > 0 ? Math.max(6, (pt.val / calc.chartMax) * 64) : 6;
          return (
            <div key={i} className="retire-chart__col" title={fmt(pt.val)}>
              <div className="retire-chart__bar" style={{ height: h }} />
              <span className="retire-chart__lbl">{pt.yr}y</span>
            </div>
          );
        })}
      </div>

      <div className="retire-cta">
        <span className="retire-cta__note">
          {accountType.includes("roth") ? "🟢 Tax-free growth (Roth)"
            : accountType === "taxable" ? "🟡 Taxable account"
            : accountType === "hsa" ? "🔵 Triple-tax-advantaged (HSA)"
            : "🔵 Tax-deferred growth"}
        </span>
        <button
          type="button"
          className="oracle-btn oracle-btn--gold"
          onClick={runAnalysis}
          disabled={disabled}
          data-testid="retire-run-analysis"
        >
          {disabled ? "Thinking…" : "🔮 LOGIX-2 Full Analysis →"}
        </button>
      </div>
    </div>
  );
}
